import React, { useEffect, useRef, useState } from 'react';
import '../Css/Reviewsection.css';
// --- NEW IMPORTS ---
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { API_URL } from '../utils/Function';

export default function Reviewsection() {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const cardsRef = useRef([]);
  const reviewsRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- NEW STATE FOR IMAGE ERRORS ---
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/review.php/get`);
      const data = await response.json();
      if (data.success) {
        setReviews(data.reviews.map(rev => ({
          name: rev.name,
          location: rev.location,
          image: rev.image ? `${API_URL}/uploads/reviews/${rev.image}` : null,
          text: rev.text
        })));
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  // --- NEW: STYLE OBJECT FOR FALLBACK ICON ---
  const fallbackStyle = {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'white',
    fontSize: '3rem' // Adjust size as needed
  };

  // --- NEW: HANDLER FOR IMAGE ONERROR EVENT ---
  const handleImageError = (index) => {
    setImageErrors(prevErrors => ({
      ...prevErrors,
      [index]: true
    }));
  };

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width > 768 && width <= 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (headerRef.current) {
              headerRef.current.classList.add('animate');
            }
            cardsRef.current.forEach((card) => {
              if (card) {
                card.classList.add('animate');
              }
            });
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentSection = sectionRef.current;

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);



  // Track scroll position
  useEffect(() => {
    if (!reviewsRef.current) return;

    const handleScroll = () => {
      const container = reviewsRef.current;
      const scrollLeft = container.scrollLeft;
      const cards = container.querySelectorAll('.review-card');

      if (cards.length > 0) {
        const cardWidth = cards[0].offsetWidth;
        const style = window.getComputedStyle(container);
        const gap = parseInt(style.gap || '0', 10) || 20;
        const index = Math.round(scrollLeft / (cardWidth + gap));
        setActiveIndex(index);
      }
    };

    const container = reviewsRef.current;
    container.addEventListener('scroll', handleScroll);

    return () => container.removeEventListener('scroll', handleScroll);
  }, []);



  const scrollToCard = (index) => {
    if (!reviewsRef.current) return;

    const container = reviewsRef.current;
    const cards = container.querySelectorAll('.review-card');

    if (cards[index]) {
      const cardWidth = cards[index].offsetWidth;
      const style = window.getComputedStyle(container);
      const gap = parseInt(style.gap || '0', 10) || 20;
      const scrollPosition = index * (cardWidth + gap); // Original line
      // The provided "Code Edit" snippet for CSS was malformed and cannot be inserted here.
      // The instruction "update the CSS to center the cards" implies a change to the CSS file.
      // As only the JS file content is provided, I will only apply the JS change (removing slice).
      // The CSS part of the instruction cannot be applied to this JS file.

      container.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
      setActiveIndex(index);
    }
  };

  const handleDotClick = (dotIndex) => {
    // Determine target card index based on screen size
    // For both mobile (individual dots) and desktop (start/end dots 0 & 1), 
    // the dotIndex maps directly to the target card index (0 or 1, or 0..3)
    const targetCardIndex = dotIndex;

    scrollToCard(targetCardIndex);
  };

  return (
    <div className="review-section" ref={sectionRef}>
      <div className="review-container">
        <div className="review-header" ref={headerRef}>
          <h2 className="main-heading">Client Testimonials</h2>
          <h2 className="sub-heading">What Say About Us</h2>
          <p className="description">
            We take step wise for your every platform. we helping to client
            <br />
            with our talented expert.
          </p>
        </div>
      </div>

      <div className="reviews-wrapper">
        <div
          className={`reviews ${isMobile || isTablet ? 'scrollable' : 'large-screen-scrollable'}`}
          ref={reviewsRef}
        >
          {loading ? (
            <div className="text-center py-5 w-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-5 w-100">
              <p>No reviews available yet.</p>
            </div>
          ) : (
            reviews.map((review, index) => (
              <div
                className={`review-card card-${(index % 4) + 1}`}
                key={index}
                ref={(el) => (cardsRef.current[index] = el)}
              >
                {/* --- IMAGE LOGIC --- */}
                {(imageErrors[index] || !review.image) ? (
                  <div
                    className="client-photo client-photo-fallback"
                    style={fallbackStyle}
                  >
                    <FontAwesomeIcon icon={faUser} style={{ height: '50%', width: '50%' }} />
                  </div>
                ) : (
                  <img
                    src={review.image}
                    alt={review.name}
                    className="client-photo"
                    onError={() => handleImageError(index)}
                  />
                )}
                <h4>{review.name}</h4>
                <p className="client-location">{review.location}</p>
                <img src='/images/blue-quote.png' alt='quote-icon' className="quote-icon" />
                <p className="review-text">{review.text}</p>
              </div>
            ))
          )}
        </div>

        <div className="dots-container" style={{ display: reviews.length > 1 ? 'flex' : 'none' }}>
          {Array.from({ length: (isMobile || isTablet) ? reviews.length : (reviews.length > 3 ? (reviews.length - 2) : 1) }).map((_, dotIndex) => {
            const isDesktop = !isMobile && !isTablet;
            let isActive = false;

            if (isDesktop) {
              // On desktop, dotIndex represents the starting card index of the view
              isActive = activeIndex === dotIndex;
            } else {
              // Mobile/Tablet: 1 dot per card
              isActive = activeIndex === dotIndex;
            }

            return (
              <button
                key={dotIndex}
                className={`dot ${isActive ? 'active' : ''}`}
                onClick={() => handleDotClick(dotIndex)}
                aria-label={`Go to review group ${dotIndex + 1}`}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}